import { Injectable } from '@angular/core';
import { Renderer2 } from '@angular/core';
import { Grid, GridOptions } from 'ag-grid-community';
import { DropdownOperationService } from './dropdown-operation.service';
import { SharedOperationService } from './shared-operation.service';
import { FormBuilderUDFService } from './form-builder-udf-service';
import moment from 'moment';
import Swal from 'sweetalert2';
import { NgxSpinnerService } from 'ngx-spinner';

@Injectable({
  providedIn: 'root'
})
export class ListOperationService {
  jsonData: any;
  dataFromShared: any;
  agGridInstances: { gridName: string; api: any }[] = [];
  renderer: Renderer2;
  frameworkComponents: any;

  constructor(
    private sharedService: SharedOperationService,
    private formService: FormBuilderUDFService,private dropdownOperationService: DropdownOperationService
  ) {
    this.agGridInstances = [];
    this.sharedService.formViewerData$.subscribe((data) => {
      if (data) {
        this.dataFromShared = data;
      }
    });
  }


async createListGrid(component: any, container: any) {
  const root: HTMLElement | null =
    container ?? this.dataFromShared?.nativeElement ?? null;

  if (!root) {
    console.warn('No root container for', component);
    return;
  }

  setTimeout(() => {
    const fieldName = component.element?.field_name;
    const gridIdByWrapper = `grid_${component.id}`;

    const ids = Array.from(root.querySelectorAll('[id]')).map(
      (el: any) => el.id
    );
    console.log('>>> IDs under root:', ids);

    // 1) Try legacy pattern: id = field_name (dimensional_characteristics, lab_testing)
    let agGridElement =
      fieldName && root.querySelector<HTMLElement>(`#${fieldName}`);

    // 2) If not found, try new pattern: id = grid_<wrapperId> (grid_793d1f37-...)
    if (!agGridElement) {
      agGridElement = root.querySelector<HTMLElement>(`#${gridIdByWrapper}`);
    }

    if (!agGridElement) {
      console.warn(
        'AG Grid container not found for',
        fieldName,
        'or',
        gridIdByWrapper
      );
      return;
    }

    const elementConfig = component.element;
    console.log('Creating grid for', elementConfig.field_name);

    if (elementConfig.datasourceUrl) {
      this.fetchDataFromApi(elementConfig, root);
    } else {
      this.createGridAndAddToInstances(elementConfig, root, agGridElement);
    }
  }, 100);
}








  createGridAndAddToInstances(elementConfig: any, container: any, agGridElement: any) {
    const processedColumnDefs = this.processColumnDefs(elementConfig.columnDefs);

    const gridOptions: GridOptions = {
      animateRows: true,
      pagination: true,
      columnDefs: processedColumnDefs,
      rowData: elementConfig.rowData,
      onGridReady: (params) => {
        this.addGridInstance(elementConfig.field_name, params.api);
        params.api.sizeColumnsToFit();
      }
    };

    new Grid(agGridElement, gridOptions);
  }

  processColumnDefs(columnDefs: any[]): any[] {
    return columnDefs.map(col => {
      // Handle Date columns
      if (col.field === 'date' || col.type === 'date' || col.cellEditor === 'agDateInput') {
        return {
          ...col,
          type: 'text',
          valueFormatter: (params: any) => {
            if (params.value) {
              const date = new Date(params.value);
              if (!isNaN(date.getTime())) {
                return moment(date).format('YYYY-MM-DD HH:mm:ss');
              }
            }
            return params.value;
          },
          cellRenderer: (params: any) => {
            if (params.value) {
              const date = new Date(params.value);
              if (!isNaN(date.getTime())) {
                return moment(date).format('YYYY-MM-DD HH:mm:ss');
              }
            }
            return params.value;
          }
        };
      }
      
      // Handle Checkbox columns
      if (col.cellEditor === 'agCheckboxCellEditor' || col.type === 'checkbox' || col.cellEditorCheckbox) {
        return {
          ...col,
          cellRenderer: (params: any) => {
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            
            // Handle various truthy values
            const value = params.value;
            if (typeof value === 'string') {
              checkbox.checked = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
            } else if (typeof value === 'boolean') {
              checkbox.checked = value;
            } else if (typeof value === 'number') {
              checkbox.checked = value === 1;
            } else {
              checkbox.checked = Boolean(value);
            }
            
            // Add event listener to update cell value
            checkbox.addEventListener('change', (e) => {
              const target = e.target as HTMLInputElement;
              params.setValue(target.checked);
            });
            
            return checkbox;
          },
          valueFormatter: undefined // Remove any value formatter for checkboxes
        };
      }
      
      // Handle Dropdown/Select columns
      if (col.cellEditor === 'agSelectCellEditor' || col.type === 'select' || col.cellEditorSelect) {
        return {
          ...col,
          valueFormatter: (params: any) => {
            const value = params.value;
            
            // If value is already processed as text
            if (typeof value === 'string' && !value.trim().startsWith('{')) {
              return value;
            }
            
            // Try to parse JSON object {text, value}
            if (typeof value === 'string' && value.trim().startsWith('{')) {
              try {
                const parsed = JSON.parse(value);
                return parsed.text || parsed.value || value;
              } catch (e) {
                return value;
              }
            }
            
            // If it's already an object
            if (value && typeof value === 'object') {
              return value.text || value.value || JSON.stringify(value);
            }
            
            return value;
          }
        };
      }
      
      return col;
    });
  }

  // Modify your fetchDataFromApi method like this:
  async fetchDataFromApi(element: any, container: any): Promise<void> {
    let params = null;
    const tableName = await this.sharedService.getTableName(element.tableName);

    if (element?.listtype === 'dataviewlist') {
      params = {
        formId: localStorage.getItem('PageInfo'),
        filterJson: ''
      };
    } else if (element?.listtype === 'reportlist') {
      if (!tableName) {
        console.error('Table name could not be fetched.');
        return;
      }
      params = {
        formId: tableName,
        filterJson: JSON.stringify(this.sharedService.getFormValuesForFilter(this.dataFromShared?.container))
      };
    } else {
      params = this.sharedService.constructDependentParams(element.field_name, container, this.getDropdownConfig.bind(this));
    }

    try {
      const data = await this.sharedService.fetchDataFromAPI(params, element.datasourceUrl, element.requestType);
      const gridData = element.resultsetName && element.resultsetName in data ? data[element.resultsetName] : data;

      const processedColumnDefs = this.processColumnDefs(element.columnDefs);
      const processedRowData = gridData.map((data: any, index: number) =>
        this.transformData(data, index, processedColumnDefs));

      if (element?.buttonEdit || element.buttonDelete || element?.buttonView) {
        this.addActionColumn(element, processedColumnDefs);
      }

      const gridElement = container?.querySelector(`#${element.field_name}`);
      if (!gridElement) return;

      // FIRST: Check if grid already exists and destroy it
      this.destroyGridInstance(element.field_name, gridElement);

      // THEN: Create new grid instance
      this.createNewGridInstance(gridElement, element.field_name, processedColumnDefs, processedRowData);

    } catch (error) {
      console.error(`Error fetching data for ${element.field_name}:`, error);
      throw error;
    }
  }

  // Add this new method to destroy existing grid
  private destroyGridInstance(gridName: string, gridElement: any): void {
     delete this.agGridInstances[gridName];

  // Destroy the actual AG Grid instance
  if (gridElement.__agGridInstance) {
    gridElement.__agGridInstance.api.destroy();
    delete gridElement.__agGridInstance;
  }

  // Clear the grid container
  while (gridElement.firstChild) {
    gridElement.removeChild(gridElement.firstChild);
  }
  }

  // Modify createNewGridInstance to store reference
  createNewGridInstance(gridElement: any, gridName: string, columnDefs: any[], rowData: any[]) {
    const gridOptions: GridOptions = {
      animateRows: true,
      pagination: true,
      columnDefs: columnDefs,
      rowData: rowData,
      onGridReady: (params) => {
        // Store reference on the DOM element
        gridElement.__agGridInstance = params;
        this.addGridInstance(gridName, params.api);
        params.api.sizeColumnsToFit();
      }
    };

    new Grid(gridElement, gridOptions);
  }

  transformData(data: any, index: number, columnDefs: any[]): any {
    const transformedData: any = {
      id: data.SubmissionID || index,
    };

    columnDefs.forEach((columnDef) => {
      const key = columnDef.field;
      if (!key || !(key in data)) return;

      let value = data[key];

      if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && 'value' in parsed && 'text' in parsed) {
            transformedData[key] = parsed.text;
            transformedData[`${key}_value`] = parsed.value;
            return;
          }
        } catch (e) {
          console.error(`Failed to parse dropdown value for ${key}:`, e);
        }
      }

      if (this.isDateString(value)) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          // Using toISOString and replace
          transformedData[key] = moment(date).format('YYYY-MM-DD HH:mm:ss');
        } else {
          transformedData[key] = value;
        }
      } else {
        transformedData[key] = value;
      }
    });

    return transformedData;
  }


  addGridInstance(gridName: string, api: any) {
  this.agGridInstances[gridName] = api;
}

  getGridInstance(gridName: string) {
     return this.agGridInstances[gridName];
  }
  addActionColumn(element: any, columnDefs: any[]) {
    if (columnDefs.some(col => col.headerName === 'Actions')) return;

    const actionsColumn = {
      headerName: 'Actions',
      cellRenderer: (params: any) => {
        const eDiv = document.createElement('div');

        if (element?.buttonEdit) {
          const editButton = document.createElement('button');
          editButton.innerHTML = '<i class="fa fa-edit"></i>';
          editButton.className = 'btn btn-primary btn-sm';
          editButton.style.marginRight = '5px';
          editButton.title = 'Edit';
          editButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onEdit(params, this.dataFromShared?.container?.nativeElement);
          });
          eDiv.appendChild(editButton);
        }

        if (element?.buttonDelete) {
          const deleteButton = document.createElement('button');
          deleteButton.innerHTML = '<i class="fa fa-trash"></i>';
          deleteButton.className = 'btn btn-danger btn-sm';
          deleteButton.style.marginRight = '5px';
          deleteButton.title = 'Delete';
          deleteButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onDelete(params);
          });
          eDiv.appendChild(deleteButton);
        }

        if (element?.buttonView) {
          const viewButton = document.createElement('button');
          viewButton.innerHTML = '<i style="color:white" class="fa fa-eye"></i>';
          viewButton.style.backgroundColor = 'green';
          viewButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onView(params);
          });
          eDiv.appendChild(viewButton);
        }

        return eDiv;
      },
      width: 150
    };

    columnDefs.push(actionsColumn);
  }

  getGridData(): { [gridName: string]: any[] } {
  const result: { [gridName: string]: any[] } = {};

  Object.keys(this.agGridInstances).forEach(gridName => {
    const api = this.agGridInstances[gridName];
    const gridData: any[] = [];
    api.forEachNode((node: any) => {
      gridData.push(node.data);
    });
    result[gridName] = gridData;
  });

  return result;
}

  async onEdit(data: any, container: any) {
    localStorage.setItem('rowId', data?.data?.id);
    const formContainer = container?.querySelector('form');
    if (!formContainer) return;

    const inputs = formContainer.querySelectorAll('input, select, textarea');

    for (const input of inputs) {
      const key = input.name;
      if (key && data.data[key] !== undefined) {
        switch (input.type) {
          case 'radio':
            // Radio button handling (working)
            input.checked = input.value === String(data.data[key]);
            break;

          case 'checkbox':
            // Special handling for string "true"/"false" values
            if (typeof data.data[key] === 'string') {
              input.checked = data.data[key].toLowerCase() === 'true';
            } else {
              // Fallback to boolean handling
              input.checked = Boolean(data.data[key]);
            }
            break;

          case 'date':
            input.value = new Date(data.data[key]).toISOString().split('T')[0];
            break;

          default:
            if (input.tagName === 'SELECT') {
              const value = data.data[`${key}_value`] || data.data[key]?.value || data.data[key];
              input.value = value || '';
            } else {
              input.value = data.data[key];
            }
            break;
        }

        if (input.tagName === 'SELECT') {
          //const dropdownOperationService = new DropdownOperationService(this.formService, this.sharedService);
          await this.dropdownOperationService.onDropdownChange(input.name, container, this.renderer);
        }
      }
    }
  }

  onView(params: any) {
    console.log('View data:', params.data);
    // Implement view functionality
  }

  onDelete(data: any) {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this record!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6'
    }).then((result) => {
      if (result.isConfirmed) {
        const key = {
          SubmissionID: data.data.id,
          formId: localStorage.getItem('PageInfo'),
        };
        this.formService.DeleteFormData(key).subscribe({
          next: async (response: any) => {
            Swal.fire('Deleted!', 'The record has been deleted.', 'success');
            this.refreshGrid();
          },
          error: (error: any) => {
            console.error('Error deleting form data:', error);
            Swal.fire('Error!', 'Failed to delete the record.', 'error');
          }
        });
      }
    })
  }



  refreshGrid() {
    for (const component of this.dataFromShared?.json_data?.added_components) {
      if (component.addedComponents) {
        for (const innerComponents of component.addedComponents) {
          for (const innerComponent of innerComponents) {
            if (innerComponent.elementName === 'list' && innerComponent.element.datasourceUrl) {
              this.createListGrid(innerComponent, this.dataFromShared?.container?.nativeElement);
            }
          }
        }
      }
    }
  }

  getDropdownConfig(dropdownName: string): any {
    let config = null;
    this.jsonData = this.jsonData === undefined ? this.dataFromShared?.json_data : this.jsonData;
    this.jsonData?.added_components?.forEach((component: any) => {
      if (component.addedComponents) {
        component.addedComponents.forEach((innerComponents: any[]) => {
          innerComponents.forEach((innerComponent: any) => {
            if ((innerComponent.elementName === 'dropdown' || innerComponent.elementName === 'list') &&
              innerComponent.element.field_name === dropdownName) {
              config = innerComponent;
            }
          });
        });
      }
    });
    return config;
  }

  isDateString(value) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      return !isNaN(new Date(value).getTime());
    }
    return false;
  }
}